import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Paper, Typography, Stack, Button, Box, Divider, Chip,
  CircularProgress, Alert, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, List, ListItem, ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HistoryIcon from '@mui/icons-material/History';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { api } from '../../api';
import dayjs from 'dayjs';

const bookingStart = (b) =>
  dayjs(`${dayjs(b.date).format('YYYY-MM-DD')}T${String(b.time).slice(0, 5)}`);

const servicesLabel = (b, lang) =>
  Array.isArray(b.services) ? b.services.filter(Boolean).map((s) => pickServiceName(s, lang)).join(', ') : '';

const totalPrice = (b) =>
  Array.isArray(b.services)
    ? b.services.filter(Boolean).reduce((sum, s) => sum + Number(s.price || 0), 0)
    : 0;

// Перенести можна не пізніше ніж за 24 год до візиту
const RESCHEDULE_MIN_HOURS = 24;
const canReschedule = (b) =>
  b.status === 'active' && bookingStart(b).isAfter(dayjs().add(RESCHEDULE_MIN_HOURS, 'hour'));
const serviceIdsOf = (b) =>
  Array.isArray(b.services) ? b.services.filter(Boolean).map((s) => s.id) : [];

export default function Cabinet() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // редагування профілю
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  // перенесення запису
  const [rsBooking, setRsBooking] = useState(null); // бронь, яку переносимо
  const [rsDate, setRsDate] = useState(null); // dayjs
  const [rsSlots, setRsSlots] = useState([]);
  const [rsTime, setRsTime] = useState('');
  const [rsBusy, setRsBusy] = useState(false);
  const [rsError, setRsError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [user, list] = await Promise.all([api.getCurrentUser(), api.getBookings()]);
      if (!user) { navigate('/login'); return; }
      setMe(user);
      setBookings(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || t('cabinet.loadError'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Вільні слоти при зміні дати/броні для перенесення (хук — до будь-яких return)
  useEffect(() => {
    if (!rsBooking || !rsDate) return;
    let alive = true;
    (async () => {
      setRsSlots([]);
      setRsTime('');
      try {
        const res = await api.getAvailability(
          rsBooking.masterId,
          rsDate.format('YYYY-MM-DD'),
          serviceIdsOf(rsBooking)
        );
        if (alive) setRsSlots(res.slots || []);
      } catch {
        if (alive) setRsSlots([]);
      }
    })();
    return () => { alive = false; };
  }, [rsBooking, rsDate]);

  const now = dayjs();
  const upcoming = bookings
    .filter((b) => b.status === 'active' && bookingStart(b).isAfter(now))
    .sort((a, b) => bookingStart(a).valueOf() - bookingStart(b).valueOf());
  const past = bookings
    .filter((b) => !bookingStart(b).isAfter(now))
    .sort((a, b) => bookingStart(b).valueOf() - bookingStart(a).valueOf());

  const handleCancel = async (id) => {
    if (!window.confirm(t('cabinet.cancelConfirm'))) return;
    try {
      await api.cancelBooking(id);
      await load();
    } catch (e) {
      setError(e.message || t('cabinet.cancelError'));
    }
  };

  const openEdit = () => {
    setEditName(me.name || '');
    setEditEmail(me.email || '');
    setEditOpen(true);
  };

  const saveProfile = async () => {
    setEditBusy(true);
    try {
      await api.updateUser(me.id, { name: editName, email: editEmail || undefined });
      setEditOpen(false);
      await load();
    } catch (e) {
      setError(e.message || t('cabinet.saveError'));
    } finally {
      setEditBusy(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  const openReschedule = (b) => {
    setRsBooking(b);
    setRsError('');
    setRsTime('');
    setRsSlots([]);
    setRsDate(bookingStart(b));
  };

  const confirmReschedule = async () => {
    if (!rsTime) { setRsError(t('cabinet.pickSlot')); return; }
    setRsBusy(true);
    setRsError('');
    try {
      await api.updateBooking(rsBooking.id, {
        date: rsDate.format('YYYY-MM-DD'),
        time: rsTime,
      });
      setRsBooking(null);
      await load();
    } catch (e) {
      setRsError(e.message || t('cabinet.rescheduleError'));
    }
    setRsBusy(false);
  };

  // Картка запису. Рендеримо ІНЛАЙН-ВИКЛИКОМ ({BookingCard(...)}), а не як
  // <BookingCard/>, щоб повторний рендер не перемонтовував піддерево (інакше
  // будь-які вкладені інпути втрачали б фокус на кожному натисканні).
  const BookingCard = ({ b, cancellable }) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('cabinet.dateAtTime', { date: bookingStart(b).format('DD.MM.YYYY'), time: bookingStart(b).format('HH:mm') })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cabinet.master')} {b.masterName || '—'}
          </Typography>
          <Typography variant="body2">{servicesLabel(b, i18n.language)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {b.durationMinutes} {t('common.min')} · {formatMoney(totalPrice(b))}
          </Typography>
        </Box>
        <Stack spacing={0.5} alignItems="flex-end">
          {canReschedule(b) && (
            <Button size="small" startIcon={<EditCalendarIcon />} onClick={() => openReschedule(b)}>
              {t('cabinet.reschedule')}
            </Button>
          )}
          {cancellable && (
            <Button color="error" size="small" onClick={() => handleCancel(b.id)}>
              {t('common.cancel')}
            </Button>
          )}
          {cancellable && !canReschedule(b) && (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 170, textAlign: 'right' }}>
              {t('cabinet.rescheduleLocked')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Профіль */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{t('cabinet.greeting', { name: me?.name })}</Typography>
          <IconButton onClick={openEdit} aria-label={t('cabinet.editProfile')}><EditIcon /></IconButton>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">{t('common.phone')}: {me?.phone || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">{t('common.email')}: {me?.email || '—'}</Typography>
        </Stack>
        
      </Paper>

      {/* Майбутні записи */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <EventAvailableIcon color="primary" />
        <Typography variant="h6">{t('cabinet.upcoming')}</Typography>
        <Chip size="small" label={upcoming.length} />
      </Stack>
      {upcoming.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 3 }}>{t('cabinet.noUpcoming')}</Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {upcoming.map((b) => (
            <React.Fragment key={b.id}>{BookingCard({ b, cancellable: true })}</React.Fragment>
          ))}
        </Stack>
      )}

      {/* Історія візитів */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h6">{t('cabinet.history')}</Typography>
        <Chip size="small" label={past.length} />
      </Stack>
      {past.length === 0 ? (
        <Typography color="text.secondary">{t('cabinet.noHistory')}</Typography>
      ) : (
        <List>
          {past.map((b) => (
            <ListItem key={b.id} divider>
              <ListItemText
                primary={`${bookingStart(b).format('DD.MM.YYYY HH:mm')} — ${servicesLabel(b, i18n.language)}`}
                secondary={`${t('cabinet.master')} ${b.masterName || '—'} · ${formatMoney(totalPrice(b))}`}
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* Перенесення запису */}
      <Dialog open={!!rsBooking} onClose={() => !rsBusy && setRsBooking(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('cabinet.reschedule')}</DialogTitle>
        <DialogContent>
          {rsBooking && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {servicesLabel(rsBooking, i18n.language)} · {rsBooking.durationMinutes} {t('common.min')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('cabinet.date')}
                  value={rsDate}
                  onChange={(d) => setRsDate(d)}
                  disablePast
                  format="YYYY-MM-DD"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
              <Typography variant="body2">{t('cabinet.pickTime')}:</Typography>
              {rsSlots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('cabinet.noSlots')}</Typography>
              ) : (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {rsSlots.map((s) => (
                    <Button
                      key={s}
                      size="small"
                      variant={rsTime === s ? 'contained' : 'outlined'}
                      onClick={() => setRsTime(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </Stack>
              )}
              {rsError && <Alert severity="error">{rsError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRsBooking(null)} disabled={rsBusy}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={confirmReschedule} disabled={rsBusy || !rsTime}>
            {rsBusy ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Редагування профілю */}
      <Dialog open={editOpen} onClose={() => !editBusy && setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('cabinet.editProfile')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('common.name')} value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth />
            <TextField label={t('common.email')} type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editBusy}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={saveProfile} disabled={editBusy}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
